// src/services/breweryService.js
// ============================================================
// BREWERY & MEMBERSHIP OPERATIONS
// ============================================================
import { supabase, handleSupabaseError } from '../supabaseClient';
import { isSuperAdmin, shouldShowSuperAdminInBrewery } from '../constants/adminConfig';

/**
 * Fetch all breweries and memberships for authenticated user
 * Super admins get ALL breweries with 'super_admin' role
 * @async
 * @returns {Array} Array of brewery objects with user's role in each
 */
export const getUserBreweries = async (userId) => {
  try {
    console.log('🔍 [getUserBreweries] Starting for userId:', userId);

    // Check if current user is super admin - they see ALL breweries
    const { data: { user } } = await supabase.auth.getUser();
    const userEmail = user?.email;
    console.log('🔍 [getUserBreweries] Current user:', { id: user?.id, email: userEmail });
    
    if (isSuperAdmin(userEmail)) {
      console.log('👑 [getUserBreweries] Super admin detected - fetching ALL breweries');
      const { data: allBreweries, error: allError } = await supabase
        .from('breweries')
        .select('id, name, description, owner_user_id, plan, seat_limit, logo_url, default_owner, created_at, water_filtration, water_filtration_batch_count')
        .order('name');
      
      if (allError) {
        console.error('❌ [getUserBreweries] Error fetching breweries for super admin:', allError);
        throw allError;
      }
      
      console.log('👑 [getUserBreweries] Super admin breweries query returned:', {
        count: allBreweries?.length || 0,
        breweries: allBreweries?.map(b => ({ id: b.id, name: b.name })) || [],
        fullData: allBreweries
      });
      
      // Return all breweries with super_admin role
      const result = {
        success: true,
        data: (allBreweries || []).map(b => ({
          ...b,
          userRole: 'super_admin',
          membershipId: null
        }))
      };
      console.log('👑 [getUserBreweries] Super admin breweries loaded:', result.data.length);
      return result;
    }

    // Fetch memberships where user is listed in bar_members
    // Use a simpler query first to avoid join performance issues
    const { data: memberData, error: memberError } = await supabase
      .from('bar_members')
      .select('id, user_id, brewery_id, role')
      .eq('user_id', userId);

    console.log('🔍 [getUserBreweries] Membership query result:', {
      error: memberError,
      dataCount: memberData?.length,
      data: memberData
    });

    if (memberError) {
      console.error('❌ [getUserBreweries] Membership query failed:', memberError);
      throw memberError;
    }

    // If no memberships found, return early
    if (!memberData || memberData.length === 0) {
      console.log('🔍 [getUserBreweries] No memberships found for user');
      return { success: true, data: [] };
    }

    // Get brewery details for the memberships
    const breweryIds = memberData.map(m => m.brewery_id);
    const { data: breweriesData, error: breweriesError } = await supabase
      .from('breweries')
      .select('id, name, description, owner_user_id, plan, seat_limit, logo_url, default_owner, created_at, water_filtration, water_filtration_batch_count')
      .in('id', breweryIds);

    console.log('🔍 [getUserBreweries] Breweries query result:', {
      error: breweriesError,
      dataCount: breweriesData?.length,
      data: breweriesData
    });

    if (breweriesError) {
      console.error('❌ [getUserBreweries] Breweries query failed:', breweriesError);
      throw breweriesError;
    }

    // Combine membership and brewery data
    const combinedData = memberData.map(membership => {
      const brewery = breweriesData?.find(b => b.id === membership.brewery_id);
      return {
        ...brewery,
        userRole: membership.role,
        membershipId: membership.id
      };
    });

    console.log('🔍 [getUserBreweries] Combined result:', {
      count: combinedData.length,
      data: combinedData
    });

    return { success: true, data: combinedData };
  } catch (error) {
    console.error('🔍 [getUserBreweries] Error:', error);
    return handleSupabaseError(error, 'getUserBreweries');
  }
};

/**
 * Create a new brewery (sets user as owner)
 * @async
 * @param {string} name - Brewery name
 * @param {string} description - Brewery description
 * @param {string} plan - Plan type ('free', 'pro', 'premium', 'exempt')
 * @returns {Object} New brewery object
 */
export const createBrewery = async (userId, name, description = '', plan = 'free') => {
  try {
    // Check if user already owns a brewery
    const { data: ownerBreweries, error: ownerError } = await supabase
      .from('bar_members')
      .select('brewery_id')
      .eq('user_id', userId)
      .eq('role', 'bar_owner');
    if (ownerError) throw ownerError;
    if (ownerBreweries && ownerBreweries.length > 0) {
      return {
        success: false,
        error: 'You can only own one brewery. Join others as a member.'
      };
    }

    const { data, error } = await supabase
      .from('breweries')
      .insert([{ owner_user_id: userId, created_by: userId, name, description, plan, default_owner: name }])
      .select()
      .single();

    if (error) throw error;

    // Auto-add owner as bar_owner member
    const { error: memberError } = await supabase
      .from('bar_members')
      .insert([{ brewery_id: data.id, user_id: userId, role: 'bar_owner' }]);

    if (memberError) {
      console.error('Failed to create brewery membership:', memberError);
      console.error('Debug Info:', { userId, breweryId: data.id });
      // If membership creation fails, we should clean up the brewery
      await supabase.from('breweries').delete().eq('id', data.id);
      throw new Error('Failed to set up brewery ownership. Brewery creation cancelled.');
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in createBrewery:', error);
    return handleSupabaseError(error, 'createBrewery');
  }
};

/**
 * Create user's first free brewery (called during onboarding)
 * @async
 * @param {string} userId - User ID
 * @param {string} breweryName - Name of the brewery
 * @returns {Object} Created brewery with user as owner
 */
export const createFirstFreeBrewery = async (userId, breweryName) => {
  try {
    // Ensure the user is authenticated
    const { data: authUser, error: authError } = await supabase.auth.getUser();
    if (authError || !authUser?.id) {
      throw new Error('User must be authenticated before creating a brewery.');
    }

    // Check if user already owns a brewery
    const { data: ownerBreweries, error: ownerError } = await supabase
      .from('bar_members')
      .select('brewery_id')
      .eq('user_id', userId)
      .eq('role', 'bar_owner');
    if (ownerError) throw ownerError;
    if (ownerBreweries && ownerBreweries.length > 0) {
      return {
        success: false,
        error: 'You can only own one brewery. Join others as a member.'
      };
    }

    // Create free brewery
    const result = await createBrewery(userId, breweryName, '', 'free');
    
    if (result.success) {
      return {
        success: true,
        data: result.data,
        message: `Welcome! Your ${breweryName} brewery is ready to go.`
      };
    } else {
      return result;
    }
  } catch (error) {
    console.error('Error in createFirstFreeBrewery:', error);
    return handleSupabaseError(error, 'createFirstFreeBrewery');
  }
};

/**
 * Get brewery members with their roles
 * Filters out super admin from all breweries except "Brewer Born"
 * @async
 * @param {string} breweryId - Brewery ID
 * @param {string} breweryName - Brewery name (optional, used for super admin filtering)
 * @returns {Array} Array of members with roles
 */
export const getBarMembers = async (breweryId, breweryName = null) => {
  try {
    // If brewery name not provided, fetch it
    let actualBreweryName = breweryName;
    if (!actualBreweryName) {
      const { data: breweryData } = await supabase
        .from('breweries')
        .select('name')
        .eq('id', breweryId)
        .single();
      actualBreweryName = breweryData?.name;
    }
    
    // Helper to filter out super admin from non-home breweries
    const filterSuperAdmin = (members) => {
      if (shouldShowSuperAdminInBrewery(actualBreweryName)) {
        return members; // Show all members including super admin
      }
      // Hide super admin from other breweries
      return members.filter(m => !isSuperAdmin(m.email));
    };

    // Preferred: Edge Function using service role for names/emails
    try {
      console.log('🔍 Attempting Edge Function get-brewery-members...');
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const { data: fnData, error: fnError } = await supabase.functions.invoke('get-brewery-members', {
        body: { breweryId },
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
      });
      console.log('🔍 Edge Function response:', { error: fnError, success: fnData?.success, memberCount: fnData?.activeMembers?.length });
      if (!fnError && fnData?.success && Array.isArray(fnData.activeMembers)) {
        console.log('✅ Using Edge Function data');
        const members = fnData.activeMembers.map(m => ({
          membershipId: m.membership_id,
          userId: m.user_id,
          role: m.role,
          email: m.email,
          name: m.name,
          createdAt: m.created_at
        }));
        return {
          success: true,
          data: filterSuperAdmin(members)
        };
      }
    } catch (e) {
      console.warn('⚠️ Edge Function get-brewery-members failed:', e.message);
    }

    // Next: fetch via RPC to include user email/name safely
    console.log('🔍 Attempting RPC get_brewery_members...');
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_brewery_members', { brewery_id: breweryId });

    if (!rpcError && Array.isArray(rpcData)) {
      console.log('✅ Using RPC data, count:', rpcData.length);
      const members = rpcData.map(m => ({
        membershipId: m.membership_id,
        userId: m.user_id,
        role: m.role,
        email: m.email,
        name: m.name,
        createdAt: m.created_at
      }));
      return {
        success: true,
        data: filterSuperAdmin(members)
      };
    }

    // Fallback: bar_members without user info
    console.log('🔍 Falling back to plain bar_members query...');
    const { data, error } = await supabase
      .from('bar_members')
      .select('id, user_id, role, created_at')
      .eq('brewery_id', breweryId);

    if (error) throw error;

    console.log('✅ Using fallback data, count:', data?.length);
    // Note: Can't filter super admin in fallback (no email available)
    return {
      success: true,
      data: (data || []).map(m => ({
        membershipId: m.id,
        userId: m.user_id,
        role: m.role,
        createdAt: m.created_at
      }))
    };
  } catch (error) {
    return handleSupabaseError(error, 'getBarMembers');
  }
};

/**
 * Invite a user to brewery (creates pending invite and sends email)
 * @async
 * @param {string} breweryId - Brewery ID
 * @param {string} email - Email to invite
 * @param {string} role - Role for invited user
 * @returns {Object} Pending invite record
 */
export const inviteBarMember = async (breweryId, email, role = 'bar_back', invitedName = '') => {
  try {
    // Get brewery name and current user info for the email
    const { data: { user } } = await supabase.auth.getUser();
    const { data: brewery } = await supabase
      .from('breweries')
      .select('name')
      .eq('id', breweryId)
      .single();

    // Get inviter's name from profiles table
    const { data: inviterProfile } = await supabase
      .from('profiles')
      .select('name')
      .eq('user_id', user.id)
      .single();
    
    const inviterName = inviterProfile?.name || user?.email || 'someone';

    // Generate unique token and expiration (7 days)
    const token = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    // Create the pending invite in database; try with invited_name first
    let insertPayload = { 
      brewery_id: breweryId, 
      invite_email: email, 
      role,
      token,
      invited_by: user.id,
      expires_at: expiresAt.toISOString(),
      invited_name: invitedName?.trim() || null
    };

    let data, error;
    ({ data, error } = await supabase
      .from('pending_invites')
      .insert([insertPayload])
      .select()
      .single());

    // If column does not exist, retry without invited_name
    if (error) {
      console.warn('⚠️ pending_invites.invited_name not available, retrying without name');
      const retryPayload = { ...insertPayload };
      delete retryPayload.invited_name;
      const retry = await supabase
        .from('pending_invites')
        .insert([retryPayload])
        .select()
        .single();
      data = retry.data;
      error = retry.error;
    }

    if (error) throw error;

    // Send invite email via Edge Function
    // Note: Email sending is optional - invite is already created successfully
    try {
      // Get the current session for auth
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      
      const { data: emailData, error: emailError } = await supabase.functions.invoke('send-invite-email', {
        body: {
          breweryId: breweryId,
          barName: brewery?.name || 'a brewery',
          email: email,
          role: role,
          inviterEmail: user?.email || 'someone',
          inviterName: inviterName,
          token: data.token
        },
        headers: accessToken ? {
          Authorization: `Bearer ${accessToken}`
        } : {}
      });

      if (emailError) {
        console.warn('⚠️ Email not sent (invite created successfully):', emailError.message);
        // Invite is created - email is optional enhancement
      } else {
        console.log('✅ Invite email sent to', email);
        console.log('✅ Email response:', emailData);
      }
    } catch (emailErr) {
      console.warn('⚠️ Email service unavailable (invite created successfully)');
      // Invite is created - continue without email
    }

    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'inviteBarMember');
  }
};

/**
 * Remove a member from brewery
 * @async
 * @param {string} membershipId - Membership ID (from bar_members)
 * @returns {Object} Success status
 */
export const removeBarMember = async (membershipId) => {
  try {
    const { error } = await supabase
      .from('bar_members')
      .delete()
      .eq('id', membershipId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'removeBarMember');
  }
};

/**
 * Update a member's role in brewery
 * @async
 * @param {string} membershipId - Membership ID
 * @param {string} newRole - New role ('bar_back', 'bartender', 'bar_manager', 'bar_owner')
 * @returns {Object} Updated membership
 */
export const updateBarMemberRole = async (membershipId, newRole) => {
  try {
    const { data, error } = await supabase
      .from('bar_members')
      .update({ role: newRole })
      .eq('id', membershipId)
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'updateBarMemberRole');
  }
};

/**
 * Get pending invites for a brewery
 * @async
 * @param {string} breweryId - Brewery ID
 * @returns {Array} Array of pending invites
 */
export const getPendingInvites = async (breweryId) => {
  try {
    const { data, error } = await supabase
      .from('pending_invites')
      .select('*')
      .eq('brewery_id', breweryId)
      .is('accepted_at', null); // Only get invites that haven't been accepted

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    return handleSupabaseError(error, 'getPendingInvites');
  }
};

/**
 * Delete a pending invite
 * @async
 * @param {string} inviteId - Pending invite ID
 * @returns {Object} Success status
 */
export const deletePendingInvite = async (inviteId) => {
  try {
    const { error } = await supabase
      .from('pending_invites')
      .delete()
      .eq('id', inviteId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'deletePendingInvite');
  }
};

/**
 * Update brewery settings
 * @async
 * @param {string} breweryId - Brewery ID
 * @param {Object} updates - Fields to update (name, description, plan, etc)
 * @returns {Object} Updated brewery
 */
export const updateBrewerySettings = async (breweryId, updates) => {
  try {
    const { data, error } = await supabase
      .from('breweries')
      .update(updates)
      .eq('id', breweryId)
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'updateBrewerySettings');
  }
};

/**
 * Accept a pending invite via token
 * @async
 * @param {string} token - Invite token from URL
 * @returns {Object} Success status with brewery info
 */
export const acceptInvite = async (token) => {
  try {
    console.log('🎫 [acceptInvite] Starting invite acceptance for token:', token);
    
    // Call Supabase function to accept invite
    const { data, error } = await supabase
      .rpc('accept_pending_invite', { invite_token: token });

    if (error) {
      console.error('❌ [acceptInvite] RPC error:', error);
      throw error;
    }

    console.log('✅ [acceptInvite] RPC success, result:', data);
    
    return { 
      success: true, 
      data,
      message: 'Invite accepted successfully'
    };
  } catch (error) {
    console.error('❌ [acceptInvite] Error:', error);
    return handleSupabaseError(error, 'acceptInvite');
  }
};

/**
 * Get pending invites FOR the current user (by email)
 * These are invites sent to the user that haven't been accepted yet
 * @async
 * @returns {Array} Array of pending invites for current user with brewery names
 */
export const getUserPendingInvites = async () => {
  try {
    // Get current user email
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.email) {
      return { success: true, data: [] };
    }

    // Fetch pending invites where invite_email matches current user
    const { data, error } = await supabase
      .from('pending_invites')
      .select(`
        id,
        brewery_id,
        invite_email,
        role,
        token,
        created_at,
        expires_at,
        breweries (
          name
        )
      `)
      .eq('invite_email', user.email.toLowerCase())
      .is('accepted_at', null)
      .gt('expires_at', new Date().toISOString());

    if (error) throw error;

    // Format data with brewery name
    const formattedData = (data || []).map(invite => ({
      ...invite,
      breweryName: invite.breweries?.name || 'Unknown Brewery'
    }));

    return { success: true, data: formattedData };
  } catch (error) {
    return handleSupabaseError(error, 'getUserPendingInvites');
  }
};

/**
 * Decline a pending invite (deletes it)
 * @async
 * @param {string} inviteId - Pending invite ID
 * @returns {Object} Success status
 */
export const declineInvite = async (inviteId) => {
  try {
    const { error } = await supabase
      .from('pending_invites')
      .delete()
      .eq('id', inviteId);

    if (error) throw error;

    return { success: true, message: 'Invite declined' };
  } catch (error) {
    return handleSupabaseError(error, 'declineInvite');
  }
};
